import { ColorOption, useColor } from 'utils'
import React, { HTMLProps } from 'react'
import styled, { css } from 'styled-components'

type TextStyleProps = {
	colorVal?: string
	textStyle: TextStyle
}

export const textStyles = css<TextStyleProps>`
	color: ${props => props.colorVal};
	text-transform: ${p => p.textStyle};

	&.__size-small {
		font-size: 1rem;
		line-height: 1.25rem;
	}
	&.__size-normal {
		font-size: 1.167rem;
		line-height: 1.3rem;
	}
	&.__size-med {
		font-size: 1.5rem;
		line-height: 1.75rem;
	}
	&.__size-big {
		font-size: 2rem;
		line-height: 2.25rem;
	}

	&.__dir-left {
		text-align: left;
	}
	&.__dir-right {
		text-align: right;
	}
	&.__dir-center {
		text-align: center;
	}

	&.__pad-top {
		padding-top: 1em;
	}
	&.__pad-bot {
		padding-bottom: 1em;
	}
	&.__m-top {
		margin-top: 1em;
	}
	&.__m-bot {
		margin-bottom: 1em;
	}
`

export const strongStyles = css`
	font-weight: 600;
`
export const italicStyles = css`
	font-style: italic;
`
export const spanStyles = css``

export const inlineStyles = css`
	&.__pad-top {
		display: inline-block;
	}
	&.__pad-bot {
		display: inline-block;
	}
	&.__m-top {
		display: inline-block;
	}
	&.__m-bot {
		display: inline-block;
	}
`

const B = styled.strong<TextStyleProps>`
	${textStyles}
	${strongStyles}
	${inlineStyles}
`
const EM = styled.em<TextStyleProps>`
	${textStyles}
	${italicStyles}
	${inlineStyles}
`
const Span = styled.span<TextStyleProps>`
	${textStyles}
	${spanStyles}
	${inlineStyles}
`

const P = styled.p<TextStyleProps>`
	${textStyles}
`

type DynamicTextProps = TextStyleProps & {
	type: TextType
	children?: React.ReactNode
	className?: string
	style?: HTMLProps<HTMLSpanElement>['style']
}

const DynamicText = ({ type, ...props }: DynamicTextProps): JSX.Element => {
	return type === 'span' ? (
		<Span {...props} />
	) : type === 'bold' ? (
		<B {...props} />
	) : type === 'italic' ? (
		<EM {...props} />
	) : (
		<P {...props} />
	)
}

type TextType = 'span' | 'bold' | 'italic' | 'p'
type TextStyle = 'none' | 'uppercase' | 'capitalize'
type TextSize = 'small' | 'normal' | 'med' | 'big'
type Dir = 'left' | 'right' | 'center'

export type TextProps = Omit<DynamicTextProps, 'type' | 'textStyle' | 'colorVal'> & {
	color?: ColorOption
	textType?: TextType
	textStyle?: TextStyle
	dir?: Dir
	size?: TextSize

	padTop?: boolean
	padBot?: boolean
	mTop?: boolean
	mBot?: boolean
}
export const Text = ({
	children,
	textType = 'span',
	textStyle = 'none',
	size = 'normal',
	dir = 'left',
	className,
	color,

	padTop,
	padBot,
	mTop,
	mBot,
	style,
}: TextProps): JSX.Element => {
	const colorVal = useColor(color)
	let textClassName = `txt${className ? ` ${className}` : ''}`

	textClassName = `${textClassName} __size-${size}`
	textClassName = `${textClassName} __dir-${dir}`

	if (padTop) textClassName = `${textClassName} __pad-top`
	if (padBot) textClassName = `${textClassName} __pad-bot`
	if (mTop) textClassName = `${textClassName} __m-top`
	if (mBot) textClassName = `${textClassName} __m-bot`

	return (
		<DynamicText
			className={textClassName}
			type={textType}
			textStyle={textStyle}
			colorVal={colorVal}
			style={style}
		>
			{children}
		</DynamicText>
	)
}
